import React from "react";
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";
import ChecklistIcon from "@mui/icons-material/Checklist";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";

const RenderQuestionDragAndDrop = ({
  question,
  selectedAnswers,
  setSelectedAnswers,
  hasSubmitted,
  feedback,
  handleNextQuestion,
  handleProceed,
  currentQuestionIndex,
  totalQuestions,
  quizDetails,
  score,
  setScore,
  setFeedback,
  setHasSubmitted,
  questionResponses,
  setQuestionResponses,
  postLearningBlockUserPassed,
  // Parent-provided helper that posts the attempt with full
  // score/questionResponses payload AND handles retry / error
  // toast / dedup via attemptRecordedRef. Returns true on success
  // (or skipped — preview / already recorded) and false on failure.
  // Use this instead of postLearningBlockUserPassed directly so the
  // DnD final-question flow matches the regular quiz flow.
  recordAttemptIfNeeded,
  employeeID,
  type,
  language
}) => {
  const t = (english, spanish, chinese) => {
    if (language === "chinese") return chinese;
    if (language === "spanish") return spanish;
    return english;
  };

  const [draggableAnswers, setDraggableAnswers] = React.useState(
    question.answers
  );
  const [targetAreaHeight, setTargetAreaHeight] = React.useState(0);
  const [imagesLoaded, setImagesLoaded] = React.useState(false);
  const targetAreaRef = React.useRef(null);

  React.useEffect(() => {
    setDraggableAnswers(question.answers);
    setSelectedAnswers([]);
    setImagesLoaded(false);
  }, [question, setSelectedAnswers]);

  React.useEffect(() => {
    const imageUrls = question.answers
      .map(([, , , image_url]) => image_url)
      .filter(Boolean);

    if (imageUrls.length === 0) {
      setImagesLoaded(true);
      return;
    }

    let loadedCount = 0;
    const imagePromises = imageUrls.map((url) => {
      return new Promise((resolve, reject) => {
        const img = new Image();
        img.onload = () => {
          loadedCount++;
          if (loadedCount === imageUrls.length) {
            setImagesLoaded(true);
          }
          resolve();
        };
        img.onerror = reject;
        img.src = url;
      });
    });

    Promise.all(imagePromises).catch((error) => {
      console.error("Error loading images:", error);
      setImagesLoaded(true);
    });
  }, [question]);

  React.useEffect(() => {
    if (imagesLoaded && targetAreaRef.current) {
      setTargetAreaHeight(targetAreaRef.current.offsetHeight);
    }
  }, [imagesLoaded]);

  const correctAnswers = question.answers
    .filter(([_, is_correct]) => is_correct)
    .map(([answer, , image_uuid]) => image_uuid || answer);

  const onDragEnd = (result) => {
    if (hasSubmitted) return;

    if (!result.destination) return;

    const { source, destination } = result;
    const sourceDroppableId = source.droppableId;
    const destinationDroppableId = destination.droppableId;

    if (
      sourceDroppableId === "answers-drop-area" &&
      destinationDroppableId === "target-drop-area"
    ) {
      const answer = draggableAnswers[source.index];
      const answerId = answer[2] || answer[0];

      setSelectedAnswers((prevSelected) => [...prevSelected, answerId]);

      setDraggableAnswers((prevAnswers) =>
        prevAnswers.filter((_, index) => index !== source.index)
      );
    }
  };

  const handleAnswerClick = (answerId) => {
    if (hasSubmitted) return;

    // Find the original answer
    const originalAnswer = question.answers.find(
      ([a, , uuid]) => uuid === answerId || a === answerId
    );

    // Remove the answer from selectedAnswers
    setSelectedAnswers((prevSelected) =>
      prevSelected.filter((id) => id !== answerId)
    );

    // Add the original answer back to draggableAnswers
    setDraggableAnswers((prevAnswers) => [...prevAnswers, originalAnswer]);
  };

  const handleNextQuestionDragAndDrop = () => {
    const isCorrect =
      correctAnswers.every((id) => selectedAnswers.includes(id)) &&
      selectedAnswers.every((id) => correctAnswers.includes(id));

    if (isCorrect) {
      setScore(score + 1);
    }

    // Mirror CompleteQuizModal's MCQ handler: build a question response
    // payload and append it to questionResponses. Without this, the
    // attempt POST sends a partial array (MCQ entries only) and the
    // backend's recompute-from-responses path treats missing drag/drop
    // questions as incorrect — incorrectly failing learners on mixed
    // quizzes. Map the answer IDs back to original choice indices so
    // the BE can reconcile against `question.answers`.
    const selectedIndices = (question.answers || [])
      .map(([answer, , image_uuid], index) => {
        const answerId = image_uuid || answer;
        return selectedAnswers.includes(answerId) ? index : null;
      })
      .filter((index) => index !== null);
    const questionResponse = {
      question_uuid: question.uuid,
      selected_answer_indices: selectedIndices,
      is_correct: isCorrect,
    };
    if (setQuestionResponses) {
      setQuestionResponses([...(questionResponses || []), questionResponse]);
    }

    setFeedback(isCorrect ? "correct" : "incorrect");
    setHasSubmitted(true);
  };

  // console.log("TARGET AREA HEIGHT", targetAreaHeight)

  return (
    <div className="w-[65%] lg:w-[85%] md:w-[90%] sm:w-[100%]">
      <div className="px-3 py-0.5 border border-slate-200 text-slate-500 text-sm rounded-lg w-fit">
        {t("Drag and drop", "Arrastrar y soltar", "拖放题")}
      </div>
      <div className="pt-3 pb-5 border-b border-slate-200">
        <h3 className="text-slate-900 text-2xl">{question.question_v2?.[0]}</h3>
      </div>
      <div className="mt-5" ref={targetAreaRef}>
        <DragDropContext onDragEnd={onDragEnd}>
          <div className="flex gap-x-8 md:gap-x-4">
            <Droppable
              droppableId="answers-drop-area"
              isDropDisabled={hasSubmitted}
              // The DnD question can render inside an embla carousel
              // slide (CompleteQuizModal). Embla applies CSS transforms
              // to its slide container, which breaks the `position:
              // fixed` positioning @hello-pangea/dnd uses for the drag
              // clone — the clone ends up positioned relative to the
              // transformed ancestor instead of the viewport, so it
              // visually "disappears" mid-drag. `renderClone` portals
              // the dragged element to document.body, escaping the
              // transform stacking context.
              renderClone={(provided, snapshot, rubric) => {
                const sourceIdx = rubric.source.index;
                const answer = draggableAnswers[sourceIdx];
                if (!answer) return null;
                const [answerText, , , image_url] = answer;
                return (
                  <div
                    ref={provided.innerRef}
                    {...provided.draggableProps}
                    {...provided.dragHandleProps}
                    style={{ ...provided.draggableProps.style }}
                    className="p-2.5 rounded-lg flex space-x-5 h-fit bg-slate-50 text-slate-600 shadow-lg"
                  >
                    {answerText ? <p>{answerText}</p> : ""}
                    {image_url && (
                      <div>
                        <img
                          src={image_url}
                          alt="Answer"
                          style={{
                            borderRadius: "10px",
                            maxHeight: "250px",
                          }}
                        />
                      </div>
                    )}
                  </div>
                );
              }}
            >
              {(provided) => (
                <div
                  ref={provided.innerRef}
                  {...provided.droppableProps}
                  className="w-1/2 flex flex-wrap justify-between gap-y-3"
                >
                  {draggableAnswers.map(
                    ([answer, isCorrect, image_uuid, image_url], index) => {
                      if (!answer && !image_url) return null;

                      const answerClass = hasSubmitted
                        ? isCorrect
                          ? "bg-emerald-500 text-white"
                          : "bg-slate-50 text-slate-600"
                        : "bg-slate-50 text-slate-600";
                      const draggableId = `draggable-${image_uuid || answer}`;

                      return (
                        <Draggable
                          key={draggableId}
                          draggableId={draggableId}
                          index={index}
                        >
                          {(provided) => (
                            <div
                              ref={provided.innerRef}
                              {...provided.draggableProps}
                              {...provided.dragHandleProps}
                              style={{
                                ...provided.draggableProps.style,
                              }}
                              className={`p-2.5 rounded-lg flex w-[48%] space-x-5 h-fit ${answerClass}`}
                            >
                              {answer ? <p>{answer}</p> : ""}
                              {image_url && (
                                <div>
                                  <img
                                    src={image_url}
                                    alt="Answer"
                                    style={{
                                      borderRadius: "10px",
                                      maxHeight: "250px",
                                    }}
                                  />
                                </div>
                              )}
                            </div>
                          )}
                        </Draggable>
                      );
                    }
                  )}
                  {provided.placeholder}
                </div>
              )}
            </Droppable>

            <Droppable
              droppableId="target-drop-area"
              isDropDisabled={hasSubmitted}
            >
              {(provided, snapshot) => (
                <div
                  ref={provided.innerRef}
                  {...provided.droppableProps}
                  style={{
                    backgroundImage: question.question_v2?.[2]
                      ? `url(${question.question_v2?.[2]})`
                      : "none",
                    backgroundSize: "cover",
                    backgroundPosition: "center",
                    height: `${targetAreaHeight}px`,
                  }}
                  className={`w-1/2 border-dashed border-2 rounded-lg flex flex-wrap justify-between gap-y-3 ${
                    snapshot.isDraggingOver
                      ? "border-emerald-500"
                      : "border-slate-300"
                  }`}
                >
                  {selectedAnswers.map((answerId) => {
                    const answerData = question.answers.find(
                      ([a, , uuid]) => uuid === answerId || a === answerId
                    );
                    const [text, isCorrect, , image_url] = answerData || [];
                    const answerClass = hasSubmitted
                      ? isCorrect
                        ? "bg-emerald-500 text-white"
                        : "bg-rose-500 text-white"
                      : "bg-slate-50 text-slate-600";

                    return (
                      <div
                        key={`selected-answer-${answerId}`}
                        className={`p-2.5 h-fit rounded-lg flex w-[48%] space-x-5 ${answerClass} ${
                          !hasSubmitted ? "cursor-pointer" : ""
                        }`}
                        onClick={() =>
                          !hasSubmitted && handleAnswerClick(answerId)
                        }
                      >
                        {text ? <p>{text}</p> : ""}
                        {image_url && (
                          <div>
                              <img
                                src={image_url}
                                alt="Answer"
                                style={{
                                  borderRadius: "10px",
                                  maxHeight: "250px",
                              }}
                            />
                          </div>
                        )}
                      </div>
                    );
                  })}
                  {provided.placeholder}
                </div>
              )}
            </Droppable>
          </div>
        </DragDropContext>
      </div>
      <div className="w-full mt-8">
        {!hasSubmitted ? (
          <div
            className="w-full px-6 py-4 rounded-lg bg-violet-700 text-white text-sm cursor-pointer text-center"
            onClick={handleNextQuestionDragAndDrop}
          >
            {t("Submit", "Enviar", "提交")}
          </div>
        ) : (
          <div
            className={`w-full px-6 py-4 rounded-lg text-white text-sm cursor-pointer flex items-center justify-center gap-x-1.5 ${
              feedback && quizDetails.show_correct_answers
                ? feedback === "correct"
                  ? "bg-emerald-500"
                  : "bg-rose-500"
                : "bg-violet-700"
            }`}
            onClick={async () => {
              if (currentQuestionIndex === totalQuestions - 1) {
                // Funnel through the parent's recordAttemptIfNeeded so
                // we get the same retry/error toast behavior as the
                // regular quiz flow. Without this, a transient failure
                // would silently drop the attempt while the learner
                // saw the celebration screen — recordAttemptIfNeeded
                // returns false on failure so we can stay on the
                // current question and let the user click again.
                if (recordAttemptIfNeeded) {
                  const ok = await recordAttemptIfNeeded();
                  if (!ok) return;
                } else if (type !== "Preview") {
                  // Backwards-compat fallback if the helper isn't
                  // wired in: keep the legacy fire-and-forget call so
                  // the attempt is at least attempted.
                  const passScore = (score / totalQuestions) * 100;
                  const passed = passScore >= quizDetails.pass_percentage;
                  postLearningBlockUserPassed(
                    quizDetails.uuid,
                    passed,
                    employeeID,
                  );
                }
              }
              handleProceed();
            }}
          >
            <p>
              {feedback && quizDetails.show_correct_answers
                ? feedback === "correct"
                  ? t("Correct", "Correcto", "正确")
                  : t("Incorrect", "Incorrecto", "错误")
                : currentQuestionIndex === totalQuestions - 1
                ? t("Results", "Resultados", "结果")
                : t("Next", "Siguiente", "下一题")}
            </p>
            <div>
              {currentQuestionIndex === totalQuestions - 1 &&
              (!feedback || !quizDetails.show_correct_answers) ? (
                <ChecklistIcon style={{ fontSize: "20px" }} />
              ) : (
                <ChevronRightIcon style={{ fontSize: "20px" }} />
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default RenderQuestionDragAndDrop;
